import { useMemo } from 'react';
import { css } from '@emotion/react';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { CheckListPopoverFilterSelector, FilterTraySection } from '@frontend/analytics-shared';
import { GroupedCheckListDropdownMenu } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Chip } from '@frontend/design-system';
import { trackingIds } from '../../tracking-ids';

interface AppointmentProviderFilterProps {
  locationId?: string[];
  value?: string[];
  onChange: (providers?: string[]) => void;
  isTrayFilter?: boolean;
}

export const AppointmentProviderFilter = ({
  locationId,
  value,
  onChange,
  isTrayFilter,
}: AppointmentProviderFilterProps) => {
  const { t } = useTranslation('analytics');

  const { data: providersList } = AnalyticsGatewayV1.Queries.useGetProvidersListQuery(
    {
      locationId,
    },
    {
      enabled: !!locationId?.length,
    }
  );

  const allProvidersList = useMemo(() => {
    if (!providersList?.data) return [];
    const allProviders = new Set<string>();
    providersList.data.forEach((loc) => {
      loc.providers?.forEach((provider) => allProviders.add(provider));
    });
    return Array.from(allProviders);
  }, [providersList?.data]);

  const providerGroupOptions = useMemo(() => {
    return allProvidersList.map((provider) => ({
      children: [],
      label: provider,
      value: provider,
    }));
  }, [allProvidersList]);

  const providerOptions = useMemo(() => {
    const options: Record<string, React.ReactNode> = {};
    allProvidersList.forEach((provider) => {
      options[provider] = (
        <Chip css={styles.chipStyles} variant='neutral'>
          {provider}
        </Chip>
      );
    });
    return options;
  }, [allProvidersList]);

  if (isTrayFilter) {
    return (
      <FilterTraySection
        label={t('Provider')}
        onReset={() => onChange(undefined)}
        disableReset={!value || value.length === 0}
      >
        <GroupedCheckListDropdownMenu
          groups={providerGroupOptions}
          label={t('Provider')}
          name='appointment-provider-filter'
          onChange={(providers) => {
            onChange(providers.length === 0 ? undefined : (providers as string[]));
          }}
          placeholder={t('Select Providers')}
          value={value || []}
          trackingIdBase={`${trackingIds.filterTrayToggle}-provider`}
        />
      </FilterTraySection>
    );
  }

  return (
    <CheckListPopoverFilterSelector
      options={providerOptions}
      label={t('Provider')}
      defaultValue={[]}
      onChange={(providers) => {
        onChange(providers.length === 0 ? undefined : (providers as string[]));
      }}
      renderIcon={(isFilterActive) => <Icon name='user-small' color={isFilterActive ? 'white' : 'default'} />}
      value={value || []}
      trackingIdBase={`${trackingIds.filtersOpen}-provider`}
      customStyles={{
        popoverMenuWrapper: {
          maxHeight: 400,
        },
        checkListWrapper: {
          maxHeight: 400,
          overflowY: 'auto',
        },
      }}
      hideSearchBar={false}
    />
  );
};

const styles = {
  chipStyles: css`
    max-width: none;
  `,
};
